import { useMemo } from 'react';
import { TriangleAlert } from 'lucide-react';
import { extractMailLinks } from '../../lib/mailLinks';

/**
 * Collapsed list of the links in an HTML message. The message frame is sandboxed so its links are inert;
 * here the real destination host is shown in front of the reader, and links whose visible text names a
 * different domain are flagged. Opens in a new tab with noopener/noreferrer.
 */
export function MailLinks({ html }: { html: string | undefined }) {
  const links = useMemo(() => extractMailLinks(html), [html]);
  if (links.length === 0) return null;
  return (
    <details className="mail-links">
      <summary>Links in this message ({links.length})</summary>
      <ul>
        {links.map((link) => (
          <li key={link.href} className="mail-link-row">
            <span className="mail-link-host" title={link.href}>
              {link.host}
            </span>
            {link.mismatch && (
              <span className="mail-link-warn" title="The link text names a different site than the link goes to">
                <TriangleAlert size={12} aria-hidden /> Link text does not match destination
              </span>
            )}
            {link.punycode && (
              <span className="mail-link-warn" title="The domain uses international characters, which can imitate another site">
                <TriangleAlert size={12} aria-hidden /> Look-alike domain risk
              </span>
            )}
            <a href={link.href} target="_blank" rel="noopener noreferrer" className="mail-link-anchor">
              {link.text || link.href}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}
